import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { OrganisationInterface } from 'src/app/model/organisations/organisation-interface';

import {
  BautagebuchStatus,
  DataspaceAccessRequestDto,
  DataspaceAgreementDto,
  DataspaceAssetDetailDto,
  DataspaceAssetResponseDto,
  ReportLifecycleStatus,
  SubmissionResponseDto
} from '../../models/bautagebuch.models';
import { BautagebuchService } from '../../services/bautagebuch.service';
import { BautagebuchDialogLauncherService } from '../../services/bautagebuch-dialog-launcher.service';
import {
  DataspaceDataSectionVm,
  DataspaceWorkspaceStat,
  NuReportListItemVm
} from '../../models/dataspace-workspace.models';
import {
  buildDataspaceSections
} from '../../utils/dataspace-section-summary.util';

@Component({
  standalone: false,
  selector: 'app-dataspace-nu-reports-view',
  templateUrl: './dataspace-nu-reports-view.component.html',
  styleUrls: ['./dataspace-nu-reports-view.component.scss']
})
export class DataspaceNuReportsViewComponent implements OnChanges {
  @Input() organisationId: number | null = null;
  @Input() organisationName = '';
  @Input() memberships: OrganisationInterface[] = [];
  @Input() refreshNonce = 0;

  loading = false;
  busy = false;
  error = '';
  reports: NuReportListItemVm[] = [];
  selectedReport: NuReportListItemVm | null = null;
  selectedDetail: DataspaceAssetDetailDto | null = null;
  statusFilter = '';
  showHiddenReports = false;
  hiddenReportIds = new Set<string>();

  constructor(
    private readonly bautagebuchService: BautagebuchService,
    private readonly launcher: BautagebuchDialogLauncherService,
    private readonly cd: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.organisationId || changes.refreshNonce) && this.organisationId) {
      this.loadHiddenReports();
      this.loadWorkspace();
    }
  }

  get stats(): DataspaceWorkspaceStat[] {
    const counts = new Map<string, number>();
    for (const report of this.filteredReports) {
      counts.set(report.status, (counts.get(report.status) || 0) + 1);
    }
    return Array.from(counts.entries())
      .filter(([_, value]) => value > 0)
      .map(([label, value]) => ({ label, value }));
  }

  get filteredReports(): NuReportListItemVm[] {
    return this.reports.filter((report) => {
      if (!this.showHiddenReports && this.hiddenReportIds.has(report.id)) {
        return false;
      }
      if (this.statusFilter && report.status !== this.statusFilter) {
        return false;
      }
      return true;
    });
  }

  get selectedAccessRequests(): DataspaceAccessRequestDto[] {
    return [];
  }

  get selectedAgreements(): DataspaceAgreementDto[] {
    return [];
  }

  openNewReport(): void {
    this.launcher.openWorkspace({
      workspaceKey: 'tagesbericht-nu',
      viewKey: 'nu',
      messageType: 'tagesbericht.nachunternehmer.0001',
      sectionPath: 'tagesbericht-nu-simplified',
      sectionTitle: 'Tagesbericht (Nachunternehmer)',
      serviceType: 'tagesbericht-nu',
      serviceName: 'Tagesbericht NU',
      mode: 'new',
      organisationId: this.organisationId || undefined,
      organisationName: this.organisationName
    }).afterClosed().subscribe(() => this.loadWorkspace());
  }

  openSelectedReport(): void {
    if (!this.selectedReport || this.selectedReport.status === 'ARCHIVIERT') {
      return;
    }

    this.launcher.openWorkspace({
      workspaceKey: 'tagesbericht-nu',
      viewKey: 'nu',
      messageType: 'tagesbericht.nachunternehmer.0001',
      sectionPath: 'tagesbericht-nu-simplified',
      sectionTitle: 'Tagesbericht (Nachunternehmer)',
      serviceType: 'tagesbericht-nu',
      serviceName: 'Tagesbericht NU',
      draftId: this.selectedReport.draftId,
      mode: this.selectedReport.draftId ? 'edit' : 'new',
      existingFieldValues: this.selectedReport.draft?.fieldValues || undefined,
      organisationId: this.organisationId || undefined,
      organisationName: this.organisationName
    }).afterClosed().subscribe(() => this.loadWorkspace());
  }

  openSelectedReportPreview(): void {
    if (!this.selectedReport) {
      return;
    }

    const existingFieldValues = this.selectedReport.draft?.fieldValues || this.getNormalizedSelectedDetailPayload();
    if (!existingFieldValues) {
      return;
    }

    const previewVisibleSectionIds = this.selectedReport.releasedSectionIds?.length
      ? [...this.selectedReport.releasedSectionIds]
      : undefined;

    this.launcher.openWorkspace({
      workspaceKey: 'tagesbericht-nu',
      viewKey: 'nu',
      messageType: 'tagesbericht.nachunternehmer.0001',
      sectionPath: 'tagesbericht-nu-simplified',
      sectionTitle: 'Tagesbericht (Nachunternehmer)',
      serviceType: 'tagesbericht-nu',
      serviceName: 'Tagesbericht NU',
      mode: 'preview',
      organisationId: this.organisationId || undefined,
      organisationName: this.organisationName,
      existingFieldValues,
      previewVisibleSectionIds
    });
  }

  selectReport(report: NuReportListItemVm): void {
    this.selectedReport = report;
    this.selectedDetail = null;

    if (!this.organisationId || !report.assetId) {
      this.cd.detectChanges();
      return;
    }

    this.bautagebuchService.getDataspaceAssetDetail(this.organisationId, report.assetId).subscribe({
      next: (detail) => {
        this.selectedDetail = detail;
        this.cd.detectChanges();
      },
      error: () => {
        this.selectedDetail = null;
        this.cd.detectChanges();
      }
    });
  }

  finaliseSelectedReport(): void {
    if (!this.selectedReport?.draftId) {
      return;
    }
    this.openSelectedReport();
  }

  archiveSelectedReport(): void {
    if (!this.selectedReport?.draftId || !this.organisationId) {
      return;
    }

    if (!window.confirm('Möchten Sie diesen Bericht wirklich archivieren?')) {
      return;
    }

    this.busy = true;
    this.error = '';
    this.bautagebuchService.archiveBautagebuchSubmission(this.selectedReport.draftId, this.organisationId).subscribe({
      next: (updated) => {
        this.busy = false;
        const archived = this.toNuReportVm(updated, this.selectedReport!.asset || null);
        this.replaceReport(archived);
      },
      error: (err) => {
        this.busy = false;
        this.error = err?.message || 'Bericht konnte nicht archiviert werden.';
        this.cd.detectChanges();
      }
    });
  }

  deleteSelectedReport(): void {
    if (!this.selectedReport?.draftId && !this.selectedReport?.assetId) {
      return;
    }

    const destructiveDelete = this.selectedReport.status === 'FINALISIERT' || this.selectedReport.status === 'ARCHIVIERT';
    const message = destructiveDelete
      ? 'Möchten Sie diesen finalisierten oder archivierten Bericht wirklich löschen? Er wird auch aus dem Dataspace entfernt.'
      : 'Möchten Sie diesen Entwurf wirklich löschen?';

    if (!window.confirm(message)) {
      return;
    }

    this.busy = true;
    this.error = '';

    const delete$: Observable<unknown> = this.selectedReport.assetId
      ? forkJoin({
        asset: this.bautagebuchService.deleteDataspaceAsset(this.organisationId!, this.selectedReport.assetId),
        submission: this.selectedReport.draftId
          ? this.bautagebuchService.deleteBautagebuchDraft(this.selectedReport.draftId, this.organisationId!).pipe(
            catchError((err) => {
              // If submission is already gone (404), we still want to proceed with asset deletion
              if (err?.original?.status === 404) {
                return of(null);
              }
              throw err;
            })
          )
          : of(null)
      })
      : this.bautagebuchService.deleteBautagebuchDraft(this.selectedReport.draftId!, this.organisationId!);

    delete$.subscribe(
      () => {
        // Minimal delay to allow backend to settle
        setTimeout(() => {
          const deletedId = this.selectedReport?.id;
          this.busy = false;
          this.reports = this.reports.filter((report) => report.id !== deletedId);
          this.selectedReport = this.filteredReports[0] || null;
          if (this.selectedReport) {
            this.selectReport(this.selectedReport);
          } else {
            this.selectedDetail = null;
            this.cd.detectChanges();
          }
        }, 50);
      },
      (error) => {
        this.busy = false;
        this.error = error?.message || 'Bericht konnte nicht gelöscht werden.';
        this.cd.detectChanges();
      }
    );
  }

  trackByReportId(_: number, report: NuReportListItemVm): string {
    return report.id;
  }

  getStatusTone(status: string): string {
    const normalized = (status || '').trim().toUpperCase();
    switch (normalized) {
      case 'FINALISIERT':
        return 'tone-var(--app-primary)';
      case 'ARCHIVIERT':
        return 'tone-rgba(var(--app-on-surface-rgb), 0.67)';
      case 'ENTWURF':
        return 'tone-amber';
      default:
        return 'tone-rgba(var(--app-on-surface-rgb), 0.67)';
    }
  }

  formatTimestamp(value?: string | null): string {
    if (!value) {
      return 'Keine Angabe';
    }

    return new Date(value).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  asPrettyJson(value: any): string {
    return JSON.stringify(value, null, 2);
  }

  private loadWorkspace(): void {
    this.loadReports();
  }

  private loadHiddenReports(): void {
    if (!this.organisationId) {
      this.hiddenReportIds = new Set<string>();
      return;
    }
    try {
      const stored = localStorage.getItem(this.hiddenReportsStorageKey());
      const parsed = stored ? JSON.parse(stored) : [];
      this.hiddenReportIds = new Set<string>(Array.isArray(parsed) ? parsed : []);
    } catch {
      this.hiddenReportIds = new Set<string>();
    }
  }

  private storeHiddenReports(): void {
    if (!this.organisationId) {
      return;
    }
    localStorage.setItem(this.hiddenReportsStorageKey(), JSON.stringify(Array.from(this.hiddenReportIds)));
  }

  private hiddenReportsStorageKey(): string {
    return `bautagebuch.nu.hiddenReports.${this.organisationId}`;
  }

  private loadReports(): void {
    if (!this.organisationId) {
      this.reports = [];
      this.selectedReport = null;
      return;
    }

    this.loading = true;
    this.error = '';

    forkJoin({
      submissions: this.bautagebuchService.listBautagebuchSubmissions(this.organisationId, { page: 0, size: 100 }).pipe(catchError(() => of({ content: [] } as any))),
      assets: this.bautagebuchService.getMyDataspaceAssets(this.organisationId).pipe(catchError(() => of([] as DataspaceAssetResponseDto[])))
    }).subscribe({
      next: ({ submissions, assets }) => {
        this.finishLoadingReports(submissions?.content || [], assets || []);
      },
      error: (error) => {
        this.loading = false;
        this.error = error?.message || 'NU Berichte konnten nicht geladen werden.';
        this.cd.detectChanges();
      }
    });
  }

  private finishLoadingReports(submissionSummaries: SubmissionResponseDto[], assets: DataspaceAssetResponseDto[]): void {
    const submissionList = submissionSummaries.filter((submission) => this.isNuReportSubmission(submission));

    const detailRequests = submissionList.map((submission) => this.loadSubmissionDetails(submission.id, submission));
    const details$: Observable<(SubmissionResponseDto | null)[]> = detailRequests.length
      ? forkJoin(detailRequests)
      : of([]);

    details$.subscribe({
      next: (loadedSubmissions) => {
        const detailedSubmissions = loadedSubmissions.filter(
          (submission): submission is SubmissionResponseDto => !!submission
        );
        const currentSubmissionIds = new Set(detailedSubmissions.map((submission) => submission.id));
        const orphanAssetRequests = assets
          .filter((asset) => !!asset.sourceSubmissionId && !currentSubmissionIds.has(asset.sourceSubmissionId))
          .map((asset) => this.loadSubmissionDetails(asset.sourceSubmissionId, null));
        const orphanSources$: Observable<(SubmissionResponseDto | null)[]> = orphanAssetRequests.length
          ? forkJoin(orphanAssetRequests)
          : of([]);

        orphanSources$.subscribe({
          next: (orphanSourceSubmissions) => {
            this.applyLoadedReports(detailedSubmissions, orphanSourceSubmissions, assets);
          },
          error: (error) => {
            this.loading = false;
            this.error = error?.message || 'NU Berichte konnten nicht geladen werden.';
            this.cd.detectChanges();
          }
        });
      },
      error: (error) => {
        this.loading = false;
        this.error = error?.message || 'NU Berichte konnten nicht geladen werden.';
        this.cd.detectChanges();
      }
    });
  }

  private applyLoadedReports(
    detailedSubmissions: SubmissionResponseDto[],
    orphanSourceSubmissions: Array<SubmissionResponseDto | null>,
    assets: DataspaceAssetResponseDto[]
  ): void {
    const itemsById = new Map<string, NuReportListItemVm>();
    const itemsByReportId = new Map<string, NuReportListItemVm>();
    const currentSubmissions = detailedSubmissions.filter((submission) => submission.currentReportVersion !== false);
    const orphanSourcesBySubmissionId = new Map<string, SubmissionResponseDto>();

    orphanSourceSubmissions
      .filter((submission): submission is SubmissionResponseDto => !!submission)
      .forEach((submission) => orphanSourcesBySubmissionId.set(submission.id, submission));

    currentSubmissions.forEach((submission) => {
      const item = this.toNuReportVm(submission, null);
      itemsById.set(item.id, item);
      if (submission.reportId) {
        itemsByReportId.set(submission.reportId, item);
      }
    });

    assets.forEach((asset) => {
      const directMatch = currentSubmissions.find((submission) => submission.id === asset.sourceSubmissionId);
      if (directMatch) {
        const merged = this.toNuReportVm(directMatch, asset);
        itemsById.set(merged.id, merged);
        if (directMatch.reportId) {
          itemsByReportId.set(directMatch.reportId, merged);
        }
        return;
      }

      const sourceSubmission = orphanSourcesBySubmissionId.get(asset.sourceSubmissionId);
      const reportMatch =
        sourceSubmission?.reportId ? itemsByReportId.get(sourceSubmission.reportId) || null : null;

      if (reportMatch?.draft) {
        const merged = this.toNuReportVm(reportMatch.draft, asset);
        itemsById.set(merged.id, merged);
        if (reportMatch.draft.reportId) {
          itemsByReportId.set(reportMatch.draft.reportId, merged);
        }
        return;
      }

      const fallback = sourceSubmission || null;
      const item = this.toNuReportVm(fallback, asset);
      itemsById.set(item.id, item);
      if (fallback?.reportId) {
        itemsByReportId.set(fallback.reportId, item);
      }
    });

    const items = Array.from(itemsById.values()).sort(
      (left, right) => (right.updatedAt || '').localeCompare(left.updatedAt || '')
    );
    this.reports = items;

    if (!this.selectedReport && this.filteredReports.length) {
      this.selectReport(this.filteredReports[0]);
    } else if (this.selectedReport) {
      const updatedSelection = this.filteredReports.find((item) => item.id === this.selectedReport?.id);
      if (updatedSelection) {
        this.selectReport(updatedSelection);
      } else if (this.filteredReports.length) {
        this.selectReport(this.filteredReports[0]);
      } else {
        this.selectedReport = null;
        this.selectedDetail = null;
      }
    }

    this.loading = false;
    this.cd.detectChanges();
  }

  private loadSubmissionDetails(
    submissionId: string,
    fallback: SubmissionResponseDto | null
  ): Observable<SubmissionResponseDto | null> {
    return this.bautagebuchService.getBautagebuchSubmission(submissionId, this.organisationId!).pipe(
      catchError(() => of(fallback))
    );
  }

  private replaceReport(report: NuReportListItemVm): void {
    const index = this.reports.findIndex((item) => item.id === report.id);
    if (index >= 0) {
      this.reports[index] = report;
    } else {
      this.reports.unshift(report);
    }
    this.reports = [...this.reports].sort((left, right) => (right.updatedAt || '').localeCompare(left.updatedAt || ''));
    this.selectReport(report);
    this.cd.detectChanges();
  }

  private getNormalizedSelectedDetailPayload(): { [key: string]: any } | null {
    const payload = this.selectedDetail?.reportPayload;
    if (!payload) {
      return null;
    }
    const report = payload.report;
    if (report && typeof report === 'object' && !Array.isArray(report)) {
      return report as { [key: string]: any };
    }
    return payload;
  }

  private toNuReportVm(
    draft: SubmissionResponseDto | null,
    asset: DataspaceAssetResponseDto | null
  ): NuReportListItemVm {
    const fieldValues = draft?.fieldValues || {};
    const projectValues = fieldValues.project || {};
    const reportDate = fieldValues?.day?.datum || asset?.reportDate || null;
    const projectLabel = asset?.projectKey || projectValues.bezeichnung || projectValues.bezeichnungDerBaumassnahme || 'Tagesbericht ohne Projekttitel';
    const subtitleSegments = [
      projectValues.nachunternehmer || asset?.producerOrgName || this.organisationName || 'Nachunternehmer',
      projectValues.gewerk || 'Tagesbericht'
    ].filter(Boolean);
    const releasedSectionIds = asset?.manifest?.releasedSectionIds || this.extractSectionIds(fieldValues, false);
    const internalSections = buildDataspaceSections(fieldValues, ['interneFelder']);
    const sharedSections = buildDataspaceSections(fieldValues, releasedSectionIds);

    return {
      id: draft?.reportId ? `report-${draft.reportId}` : asset?.assetId || draft?.id || `nu-${projectLabel}-${reportDate || 'na'}`,
      draftId: draft?.id || asset?.sourceSubmissionId,
      assetId: asset?.assetId,
      reportDate,
      projectLabel,
      subtitle: subtitleSegments.join(' · '),
      status: this.resolveNuStatus(draft, asset),
      updatedAt: asset?.updatedAt || draft?.updatedAt || draft?.createdAt,
      projectKey: asset?.projectKey || projectLabel,
      releasedSectionIds,
      consumerCount: 0,
      consumerOrgIds: [],
      internalSections,
      sharedSections,
      draft,
      asset
    };
  }

  private resolveNuStatus(
    draft: SubmissionResponseDto | null,
    asset: DataspaceAssetResponseDto | null
  ): string {
    if (draft?.reportLifecycleStatus === ReportLifecycleStatus.ARCHIVED) {
      return 'ARCHIVIERT';
    }
    if (draft?.reportLifecycleStatus === ReportLifecycleStatus.FINALIZED) {
      return 'FINALISIERT';
    }
    if (draft?.reportLifecycleStatus === ReportLifecycleStatus.DRAFT) {
      return 'ENTWURF';
    }
    if (asset) {
      return 'FINALISIERT';
    }
    if (draft?.status === BautagebuchStatus.SUBMITTED || draft?.fieldValues?.__uiMeta?.nuStatus === 'FINALISIERT') {
      return 'FINALISIERT';
    }
    return 'ENTWURF';
  }

  private isNuReportSubmission(submission: SubmissionResponseDto): boolean {
    return submission.messageType === 'tagesbericht.nachunternehmer.0001'
      || submission.serviceType === 'tagesbericht-nu'
      || submission.serviceName === 'Tagesbericht NU';
  }

  private extractSectionIds(fieldValues: any, includeInternal: boolean): string[] {
    return Object.keys(fieldValues || {}).filter((key) => includeInternal || key !== 'interneFelder');
  }

  private resolveReleaseSections(report: NuReportListItemVm): string[] {
    if (report.releasedSectionIds.length) {
      return report.releasedSectionIds;
    }
    if (report.sharedSections.length) {
      return report.sharedSections.map((section) => section.id);
    }
    return ['project', 'day', 'weather', 'nuFirma', 'behinderungen'];
  }
}
